import React, { useEffect } from "react";
import {
  DraggableSidebar,
  IDraggableSidebarItem,
  IDraggableSidebarItemResolved,
} from "../../controls";
import { SET_SIDEBAR_VIEW, useArsContext } from "../contexts";
import { ArsSidebarFilter } from "./ArsSidebarFilter";
import { ArsSidebarItem } from "./ArsSidebarItem";
import { ArsSidebarViewRadio } from "./ArsSidebarViewRadio";
import {
  ArsSidebarItemAnalysisMethod,
  ArsSidebarItemData,
  ArsSidebarItemGroupingFactor,
  ArsSidebarItemKind,
  ArsSidebarItemOperation,
  ArsSidebarItemVariable,
} from "./interface";
import { NewItemButton } from "./NewItemButton";
import { totalSidebarItem } from "./TotalGroupingFactorSidebarItem";

export interface ArsDraggableSidebarProps {
  onItemDragStart?: (items: IDraggableSidebarItemResolved<ArsSidebarItemData>[]) => void;
  onItemDragEnd?: (items: IDraggableSidebarItemResolved<ArsSidebarItemData>[]) => void;
  showGroupingFactors?: boolean;
  showOperations?: boolean;
  showTotalGroupingFactor?: boolean;
  showVariables?: boolean;
  style?: React.CSSProperties;
}

/**
 * Renders the Ars draggable sidebar for grouping factors, operations, or variables.
 * The sidebar header switches between a radio/filter combination for table views
 * and the dataset/filter controls for the listing view.
 *
 * @param props - Component props
 * @param props.onItemDragStart - Optional callback fired when a sidebar item drag begins
 * @param props.onItemDragEnd - Optional callback fired when a sidebar item drag ends
 * @param props.showGroupingFactors - Whether the grouping-factor view is available
 * @param props.showOperations - Whether the operations view is available
 * @param props.showTotalGroupingFactor - Whether to show the total grouping factor in the grouping factors view
 * @param props.showVariables - Whether to render the variable-list sidebar
 * @param props.style - Optional CSS styles to apply to the sidebar container
 * @returns Sidebar container with the requested views and draggable items
 */
export const ArsDraggableSidebar = ({
  onItemDragStart,
  onItemDragEnd,
  showGroupingFactors = true,
  showOperations = false,
  showTotalGroupingFactor = false,
  showVariables = false,
  style,
}: ArsDraggableSidebarProps): React.ReactNode => {
  const { state, dispatch } = useArsContext();

  const currentView = state.currentSidebar.view;
  const availableViews = [
    showGroupingFactors ? "groupingFactor" : null,
    showOperations ? "operation" : null,
    showVariables ? "variable" : null,
  ].filter(Boolean) as string[];
  const filterText = state.currentSidebar.filter.toLowerCase();

  // Update current view if it is not available
  useEffect(() => {
    if (availableViews.length > 0 && !availableViews.includes(currentView)) {
      dispatch({
        operation: SET_SIDEBAR_VIEW,
        view: availableViews[0] as "groupingFactor" | "operation" | "variable",
      });
    }
  }, [availableViews, currentView, dispatch]);
  if (availableViews.length === 0) return null;

  const groupingFactorItems: IDraggableSidebarItemResolved<ArsSidebarItemGroupingFactor>[] =
    showGroupingFactors
      ? Object.values(state.groupingFactors).map((gf) => ({
          id: `ars-table-sidebar-grouping-factor-${gf.id}`,
          label: gf.description && gf.description.trim() !== "" ? gf.description.trim() : gf.name,
          data: {
            kind: ArsSidebarItemKind.GroupingFactor,
            dropItem: gf,
          },
        }))
      : [];

  const analysisMethodItems: IDraggableSidebarItemResolved<ArsSidebarItemData>[] = showOperations
    ? Object.values(state.analysisMethods)
        .filter(
          (am) =>
            !state.currentSidebar.analysisMethodId ||
            (state.currentSidebar.analysisMethodId &&
              am.id === state.currentSidebar.analysisMethodId),
        )
        .flatMap((am) => {
          const analysisItems: IDraggableSidebarItemResolved<ArsSidebarItemAnalysisMethod>[] = [
            {
              id: `ars-table-sidebar-analysis-method-${am.id}`,
              label:
                am.description && am.description.trim() !== "" ? am.description.trim() : am.name,
              data: {
                kind: ArsSidebarItemKind.AnalysisMethod,
                dropItem: am,
              },
            },
          ];
          const operationSidebarItems: IDraggableSidebarItemResolved<ArsSidebarItemOperation>[] =
            am.operations.map((operation) => ({
              id: `ars-table-sidebar-operation-${operation.id}`,
              label: operation.label ?? operation.name,
              data: {
                kind: ArsSidebarItemKind.Operation,
                dropItem: operation,
                analysisMethodId: am.id,
              },
            }));
          return [...analysisItems, ...operationSidebarItems];
        })
    : [];

  const variables: IDraggableSidebarItemResolved<ArsSidebarItemVariable>[] = showVariables
    ? state.variables
        .filter((v) => v.dataset === state.currentSidebar.dataset)
        .map((v) => ({
          id: `ars-table-sidebar-variable-${v.oid}`,
          label: v.value,
          data: {
            kind: ArsSidebarItemKind.Variable,
            dropItem: v,
          },
        }))
    : [];

  const sidebarItems: IDraggableSidebarItem<ArsSidebarItemData>[] = (
    currentView === "groupingFactor"
      ? [...(showTotalGroupingFactor ? [totalSidebarItem] : []), ...groupingFactorItems]
      : currentView === "operation"
        ? analysisMethodItems
        : variables
  ).filter((item) => item.label.toLowerCase().includes(filterText));

  return (
    <div className="d-flex justify-content-end align-items-start">
      <DraggableSidebar<ArsSidebarItemData>
        id={"ars-draggable-sidebar"}
        bodyLevel={true}
        items={sidebarItems}
        multiSelect={true}
        onItemDragEnd={onItemDragEnd}
        onItemDragStart={onItemDragStart}
        pushContent={true}
        RenderItem={ArsSidebarItem}
        resizable={true}
        show={state.currentSidebar.isOpen}
        side="right"
        style={style}
        width={state.currentSidebar.width}
        header={
          <div className="d-flex flex-column gap-2">
            <ArsSidebarViewRadio
              showGroupingFactors={showGroupingFactors}
              showOperations={showOperations}
              showVariables={showVariables}
            />
            <ArsSidebarFilter />
            <NewItemButton />
          </div>
        }
      />
    </div>
  );
};

ArsDraggableSidebar.displayName = "ArsDraggableSidebar";
